import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { loadError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { euro, fmtDate, STATUS } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import BookingDialog from "@/components/BookingDialog";
import {
  TrendingUp,
  BedDouble,
  Percent,
  Wallet,
  Plus,
  CalendarClock,
  Clock,
  LogIn,
  LogOut,
  Receipt,
  CheckCircle2,
  Theater,
  ArrowRight,
} from "lucide-react";

const KpiCard = ({ icon: Icon, label, value, sub, tint }) => (
  <Card className="p-5 border-border shadow-sm animate-fade-up">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{label}</p>
        <p className="font-heading text-2xl sm:text-3xl font-semibold mt-2 tracking-tight">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </div>
      <div className="w-10 h-10 rounded-xl grid place-items-center" style={{ background: tint }}>
        <Icon className="w-5 h-5 text-white" />
      </div>
    </div>
  </Card>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [open, setOpen] = useState(false);
  const [checkingIn, setCheckingIn] = useState(null);
  const [checkingOut, setCheckingOut] = useState(null);
  const [collecting, setCollecting] = useState(null);

  const collectBalance = async (b) => {
    setCollecting(b.id);
    try {
      await api.post("/payments", { booking_id: b.id, amount: b.balance, method: "Contanti", notes: "Saldo al check-out" });
      toast.success(`Incassati ${euro(b.balance)} da ${b.guest_name}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCollecting(null); }
  };

  const quickCheckout = async (b) => {
    if (b.balance > 0 && !window.confirm(`${b.guest_name} ha un saldo residuo di ${euro(b.balance)}. Confermare comunque il check-out?`)) return;
    setCheckingOut(b.id);
    try {
      await api.patch(`/bookings/${b.id}/status`, { status: "checkedout" });
      toast.success(`Check-out registrato per ${b.guest_name}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCheckingOut(null); }
  };

  const quickCheckin = async (b) => {
    setCheckingIn(b.id);
    try {
      await api.patch(`/bookings/${b.id}/status`, { status: "checkedin" });
      toast.success(`Check-in registrato per ${b.guest_name}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCheckingIn(null); }
  };

  const load = () => {
    api.get("/reports/summary").then((r) => setData(r.data)).catch(loadError);
    api.get("/rooms").then((r) => setRooms(r.data)).catch(loadError);
  };
  useEffect(load, []);

  const greeting = () => {
    const h = new Date().getHours();
    return h < 12 ? "Buongiorno" : h < 18 ? "Buon pomeriggio" : "Buonasera";
  };

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">Panoramica</p>
          <h1 className="font-display text-4xl sm:text-5xl mt-1">
            {greeting()}, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-muted-foreground mt-1">
            {new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </p>
        </div>
        <Button data-testid="dashboard-new-booking" onClick={() => setOpen(true)} className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-full h-11 px-5 font-heading">
          <Plus className="w-4 h-4 mr-2" /> Nuova Prenotazione
        </Button>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCard icon={Percent} label="Occupazione (mese)" value={`${data.occupancy}%`} sub={`${data.total_rooms} camere`} tint="#C86D51" />
            {isAdmin && <KpiCard icon={TrendingUp} label="Incasso mese" value={euro(data.month_income)} sub={`Totale: ${euro(data.total_income)}`} tint="#4A6B5D" />}
            <KpiCard icon={BedDouble} label="Prenotazioni attive" value={data.active_bookings} sub="confermate + check-in" tint="#1F8A5B" />
            {isAdmin && <KpiCard icon={Wallet} label="ADR / RevPAR" value={euro(data.adr)} sub={`RevPAR ${euro(data.revpar)}`} tint="#D4AF37" />}
            {!isAdmin && <KpiCard icon={Wallet} label="Camere totali" value={data.total_rooms} tint="#D4AF37" />}
          </div>

          {data.auditorium_pending && data.auditorium_pending.count > 0 && (
            <Card className="border-amber-500/40 bg-amber-50 dark:bg-amber-900/15 shadow-sm" data-testid="auditorium-pending-card">
              <div className="px-6 py-4 flex flex-wrap items-center gap-4">
                <div className="w-10 h-10 rounded-xl grid place-items-center bg-amber-500 text-white"><Theater className="w-5 h-5" /></div>
                <div className="flex-1 min-w-[200px]">
                  <h2 className="font-heading text-lg font-semibold">
                    <span data-testid="auditorium-pending-count">{data.auditorium_pending.count}</span> {data.auditorium_pending.count === 1 ? "richiesta auditorium da approvare" : "richieste auditorium da approvare"}
                  </h2>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">
                    {data.auditorium_pending.items.map((b) => `${fmtDate(b.date)} · ${b.title} (${b.requester_name})`).join("  ·  ")}
                  </p>
                </div>
                <Button data-testid="auditorium-pending-link" onClick={() => navigate("/auditorium?pending=1")} className="bg-amber-500 hover:bg-amber-600 text-white rounded-full font-heading">
                  Gestisci richieste <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </Card>
          )}

          <Card className="border-border shadow-sm" data-testid="arrivals-today-card">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-accent" />
                <h2 className="font-heading text-lg font-semibold">Arrivi di oggi</h2>
              </div>
              <span className="text-xs text-muted-foreground" data-testid="arrivals-today-count">{data.arrivals_today.length} in arrivo</span>
            </div>
            <div className="divide-y divide-border" data-testid="arrivals-today-list">
              {data.arrivals_today.length === 0 && <p className="px-6 py-8 text-center text-muted-foreground">Nessun arrivo previsto per oggi.</p>}
              {data.arrivals_today.map((b) => (
                <div key={b.id} data-testid={`arrival-today-${b.id}`} className="px-6 py-3.5 flex items-center gap-4 hover:bg-secondary/40 transition-colors">
                  <div className={`w-16 shrink-0 text-center rounded-lg py-1.5 font-heading font-semibold text-sm ${b.arrival_time ? "bg-accent text-accent-foreground" : "bg-secondary text-muted-foreground"}`}>
                    {b.arrival_time || "—:—"}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{b.guest_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{b.room_name} · {b.num_guests} ospiti{b.guest_phone ? ` · ${b.guest_phone}` : ""}</p>
                    {b.arrival_notes && <p className="text-xs text-accent truncate mt-0.5">{b.arrival_notes}</p>}
                  </div>
                  {!b.arrival_time && <span className="text-[11px] text-muted-foreground shrink-0">orario non comunicato</span>}
                  {b.status === "checkedin" ? (
                    <Badge variant="secondary" data-testid={`checked-in-badge-${b.id}`} className={`${STATUS.checkedin.bg} ${STATUS.checkedin.text} border-0 shrink-0`}>
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Arrivato
                    </Badge>
                  ) : (
                    <Button size="sm" data-testid={`quick-checkin-${b.id}`} onClick={() => quickCheckin(b)} disabled={checkingIn === b.id}
                      className="bg-primary text-primary-foreground rounded-full shrink-0 h-8">
                      <LogIn className="w-3.5 h-3.5 mr-1.5" />{checkingIn === b.id ? "…" : "Check-in"}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </Card>

          <Card className="border-border shadow-sm" data-testid="departures-today-card">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <LogOut className="w-5 h-5 text-accent" />
                <h2 className="font-heading text-lg font-semibold">Partenze di oggi</h2>
              </div>
              <span className="text-xs text-muted-foreground" data-testid="departures-today-count">{data.departures_today.length} in partenza</span>
            </div>
            <div className="divide-y divide-border" data-testid="departures-today-list">
              {data.departures_today.length === 0 && <p className="px-6 py-8 text-center text-muted-foreground">Nessuna partenza prevista per oggi.</p>}
              {data.departures_today.map((b) => (
                <div key={b.id} data-testid={`departure-today-${b.id}`} className="px-6 py-3.5 flex items-center gap-4 hover:bg-secondary/40 transition-colors">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{b.guest_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{b.room_name} · {b.num_guests} ospiti</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Saldo</p>
                    <p data-testid={`departure-balance-${b.id}`} className={`font-heading font-semibold ${b.balance > 0 ? "text-orange-600" : "text-emerald-600"}`}>
                      {b.balance > 0 ? euro(b.balance) : "Saldato"}
                    </p>
                  </div>
                  {b.status !== "checkedout" && b.balance > 0 && (
                    <Button size="sm" data-testid={`collect-balance-${b.id}`} onClick={() => collectBalance(b)} disabled={collecting === b.id}
                      className="rounded-full shrink-0 h-8 bg-accent hover:bg-accent/90 text-accent-foreground">
                      <Wallet className="w-3.5 h-3.5 mr-1.5" />{collecting === b.id ? "…" : "Incassa saldo"}
                    </Button>
                  )}
                  {b.status === "checkedout" ? (
                    <Badge variant="secondary" data-testid={`checked-out-badge-${b.id}`} className={`${STATUS.checkedout.bg} ${STATUS.checkedout.text} border-0 shrink-0`}>
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Partito
                    </Badge>
                  ) : (
                    <Button size="sm" variant={b.balance > 0 ? "outline" : "default"} data-testid={`quick-checkout-${b.id}`} onClick={() => quickCheckout(b)} disabled={checkingOut === b.id}
                      className={`rounded-full shrink-0 h-8 ${b.balance > 0 ? "border-orange-400 text-orange-700 hover:bg-orange-50" : "bg-primary text-primary-foreground"}`}>
                      <LogOut className="w-3.5 h-3.5 mr-1.5" />{checkingOut === b.id ? "…" : "Check-out"}
                    </Button>
                  )}
                  {isAdmin && <Button size="sm" variant="outline" data-testid={`receipt-${b.id}`} onClick={() => navigate(`/fatture?booking=${b.id}`)} title="Emetti o apri la ricevuta" className="rounded-full shrink-0 h-8">
                    <Receipt className="w-3.5 h-3.5 mr-1.5" />Ricevuta
                  </Button>}
                </div>
              ))}
            </div>
          </Card>

          <Card className="border-border shadow-sm">
            <div className="px-6 py-4 border-b border-border flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-accent" />
              <h2 className="font-heading text-lg font-semibold">Prossimi arrivi</h2>
            </div>
            <div className="divide-y divide-border" data-testid="upcoming-list">
              {data.upcoming.length === 0 && <p className="px-6 py-8 text-center text-muted-foreground">Nessun arrivo in programma.</p>}
              {data.upcoming.map((b) => {
                const s = STATUS[b.status] || STATUS.confirmed;
                return (
                  <div key={b.id} className="px-6 py-3.5 flex items-center justify-between gap-4 hover:bg-secondary/40 transition-colors">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-full bg-secondary grid place-items-center text-xs font-semibold font-heading shrink-0">
                        {b.guest_name.split(" ").map((x) => x[0]).slice(0, 2).join("")}
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium truncate">{b.guest_name}</p>
                        <p className="text-xs text-muted-foreground truncate">{b.room_name} · {b.num_guests} ospiti</p>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-medium">{fmtDate(b.check_in)}</p>
                      <Badge variant="secondary" className={`${s.bg} ${s.text} border-0 text-[11px]`}>{s.label}</Badge>
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </>
      )}

      <BookingDialog open={open} onOpenChange={setOpen} rooms={rooms} onSaved={load} />
    </div>
  );
}
